import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, ComposedChart } from 'recharts';

import Button from '../../../components/ui/Button';

const TreatmentEffectivenessChart = () => {
  const [chartType, setChartType] = useState('effectiveness');
  const [timeRange, setTimeRange] = useState('30-days');

  const effectivenessData = [
    {
      date: '2024-10-17',
      effectiveness: 85,
      pestPressure: 45,
      applications: 12,
      weather: 'Optimal',
      cost: 520
    },
    {
      date: '2024-10-24',
      effectiveness: 78,
      pestPressure: 62,
      applications: 18,
      weather: 'Rainy',
      cost: 680
    },
    {
      date: '2024-10-31',
      effectiveness: 92,
      pestPressure: 38,
      applications: 15,
      weather: 'Optimal',
      cost: 590
    },
    {
      date: '2024-11-07',
      effectiveness: 88,
      pestPressure: 41,
      applications: 14,
      weather: 'Windy',
      cost: 560
    },
    {
      date: '2024-11-14',
      effectiveness: 94,
      pestPressure: 32,
      applications: 11,
      weather: 'Optimal',
      cost: 480
    }
  ];

  const chartOptions = [
    { id: 'effectiveness', label: 'Effectiveness Over Time', icon: 'TrendingUp' },
    { id: 'cost-analysis', label: 'Cost Analysis', icon: 'DollarSign' },
    { id: 'correlation', label: 'Weather Correlation', icon: 'CloudRain' }
  ];

  const timeRangeOptions = [
    { id: '7-days', label: '7 Days' },
    { id: '30-days', label: '30 Days' },
    { id: '90-days', label: '90 Days' },
    { id: 'season', label: 'This Season' }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-4 agricultural-shadow">
          <p className="font-medium text-foreground mb-2">
            {new Date(label)?.toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric' 
            })}
          </p>
          {payload?.map((entry) => (
            <div key={entry?.dataKey} className="flex items-center space-x-2 text-sm">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry?.color }}
              />
              <span className="text-muted-foreground">{entry?.name}:</span>
              <span className="font-medium text-foreground">
                {entry?.dataKey === 'cost' ? `$${entry?.value}` : `${entry?.value}%`}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    switch (chartType) {
      case 'effectiveness':
        return (
          <ComposedChart data={effectivenessData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              dataKey="date" 
              tickFormatter={(value) => new Date(value)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              stroke="#6B7280"
            />
            <YAxis stroke="#6B7280" />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="effectiveness" 
              stroke="#2D5A27" 
              strokeWidth={3}
              name="Treatment Effectiveness"
              dot={{ fill: '#2D5A27', strokeWidth: 2, r: 6 }}
            />
            <Bar 
              dataKey="pestPressure" 
              fill="#F4A460" 
              opacity={0.6}
              name="Pest Pressure"
            />
          </ComposedChart>
        );
      case 'cost-analysis':
        return (
          <BarChart data={effectivenessData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              dataKey="date" 
              tickFormatter={(value) => new Date(value)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              stroke="#6B7280"
            />
            <YAxis stroke="#6B7280" />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="cost" fill="#8B4513" name="Treatment Cost ($)" />
          </BarChart>
        );
      default:
        return (
          <LineChart data={effectivenessData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              dataKey="date" 
              tickFormatter={(value) => new Date(value)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              stroke="#6B7280"
            />
            <YAxis stroke="#6B7280" />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="effectiveness" 
              stroke="#2D5A27" 
              strokeWidth={2}
              name="Effectiveness"
            />
          </LineChart>
        );
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 agricultural-shadow">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-2">
            Treatment Effectiveness Analysis
          </h2>
          <p className="text-sm text-muted-foreground">
            Comprehensive analysis of treatment performance and pest pressure correlation
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3 mt-4 lg:mt-0">
          <div className="flex bg-muted rounded-lg p-1">
            {chartOptions?.map((option) => (
              <Button
                key={option?.id}
                variant={chartType === option?.id ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setChartType(option?.id)}
                iconName={option?.icon}
                iconPosition="left"
                className="text-xs"
              >
                {option?.label}
              </Button>
            ))}
          </div>
          
          <div className="flex bg-muted rounded-lg p-1">
            {timeRangeOptions?.map((option) => (
              <Button
                key={option?.id}
                variant={timeRange === option?.id ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setTimeRange(option?.id)}
                className="text-xs"
              >
                {option?.label}
              </Button>
            ))}
          </div>
        </div>
      </div>
      <div className="h-96 w-full">
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 pt-6 border-t border-border">
        <div className="text-center">
          <div className="text-2xl font-bold text-success mb-1">94%</div>
          <div className="text-sm text-muted-foreground">Peak Effectiveness</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-primary mb-1">$540</div>
          <div className="text-sm text-muted-foreground">Avg Cost/Week</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-warning mb-1">15</div>
          <div className="text-sm text-muted-foreground">Avg Applications</div>
        </div>
      </div>
    </div>
  );
};

export default TreatmentEffectivenessChart;